/**
 * SAYM product tabs: switches tab panels (ARIA tabs pattern with arrow keys)
 * and scrolls the product row one page at a time with the side arrows.
 */
class SaymProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!this.tabs.length) return;

    // Group the buttons (rendered one per tab block) in a real tablist.
    // The list uses display: contents, so the layout does not move.
    const list = document.createElement('div');
    list.className = 'saym-tabs__list';
    list.setAttribute('role', 'tablist');
    this.tabs[0].closest('.saym-tabs__inner')?.prepend(list);
    list.append(...this.tabs);
    this.select(0);
    this.dataset.ready = '';

    this.onBlockSelect = (/** @type {Event} */ event) => {
      const index = this.panels.findIndex((panel) => event.target instanceof Node && panel.parentElement?.contains(event.target));
      if (index > -1) this.select(index);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));
    });

    this.panels.forEach((panel) => {
      const track = panel.querySelector('.saym-tabs__track');
      if (!track) return;
      panel.querySelector('[data-saym-tabs-prev]')?.addEventListener('click', () => this.#page(track, -1));
      panel.querySelector('[data-saym-tabs-next]')?.addEventListener('click', () => this.#page(track, 1));
      track.addEventListener('scroll', () => this.#updateArrows(panel), { passive: true });
      this.#updateArrows(panel);
    });

    this.#positionArrows();
    this.resizeObserver = new ResizeObserver(() => {
      this.#positionArrows();
      this.panels.forEach((panel) => this.#updateArrows(panel));
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  /** @param {number} index */
  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = !selected;
    });
    if (focus) this.tabs[index]?.focus();
    const panel = this.panels[index];
    if (panel) {
      this.#positionArrows();
      this.#updateArrows(panel);
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.select(next, true);
  }

  /**
   * @param {HTMLElement} track
   * @param {number} direction
   */
  #page(track, direction) {
    const isRtl = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: track.clientWidth * direction * (isRtl ? -1 : 1) });
  }

  /** @param {HTMLElement} panel */
  #updateArrows(panel) {
    const track = panel.querySelector('.saym-tabs__track');
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    const position = Math.abs(track.scrollLeft);
    const prev = panel.querySelector('[data-saym-tabs-prev]');
    const next = panel.querySelector('[data-saym-tabs-next]');
    if (prev) prev.disabled = position <= 2;
    if (next) next.disabled = position >= max;
  }

  /** Centre the arrows on the product image, not on the whole card */
  #positionArrows() {
    const panel = this.panels.find((p) => !p.hidden);
    const image = panel?.querySelector('.card-gallery');
    if (!image) return;
    this.style.setProperty('--saym-tabs-arrow-top', `${image.offsetHeight / 2 - 16}px`);
  }
}

if (!customElements.get('saym-product-tabs')) {
  customElements.define('saym-product-tabs', SaymProductTabs);
}
